import React, { useState } from "react";
import { Plus } from "lucide-react";
import { SCROLL_OFFSET, H2, CONTAINER } from "./sections";

const FAQS = [
  {
    question: "Is AI voice typing the same as speech recognition?",
    answer:
      "Not quite. Speech recognition is the transcription step: it identifies the words you said. AI voice typing starts there and adds text processing on top, so the result reads like finished writing rather than a raw transcript.",
  },
  {
    question: "Is Zero Typing just a transcription tool?",
    answer:
      "No. Transcription is only the first stage. Zero Typing AI also cleans up grammar, adds punctuation and paragraph breaks, and can rephrase, reorganize, or reformat what you said before it lands on the page.",
  },
  {
    question: "Can I speak naturally when using Zero Typing AI?",
    answer:
      'Yes. You do not need to dictate punctuation commands like "comma" or "new paragraph." Talk the way you would explain something to a coworker and the formatting is handled for you.',
  },
  {
    question: "Can AI voice typing improve grammar?",
    answer:
      "Yes. Filler words, false starts, and repeated phrases are common in natural speech. Zero Typing AI can tidy those up so the text you keep is closer to what you would have written by hand.",
  },
  {
    question: "Can AI voice typing rephrase what I say?",
    answer:
      "Yes. You can ask for a different tone or wording after you speak, which is useful when the same idea needs to sound one way in an email and another way in a report.",
  },
  {
    question: "Can AI voice typing format my text?",
    answer:
      "Often, yes. Paragraphs, lists, and basic structure can be applied automatically, so long-form dictation does not arrive as one unbroken block of text.",
  },
  {
    question: "Can I create my own writing style in Zero Typing AI?",
    answer:
      "Yes. You can save a custom style and reuse it, so your dictated text consistently comes out in the voice you write in rather than a generic default.",
  },
  {
    question: "Do I have to use AI formatting at all?",
    answer:
      "No. AI processing is optional. If you prefer the raw transcript exactly as you said it, you can skip the refinement stage entirely and keep the unformatted text.",
  },
  {
    question: "Is AI voice typing useful if I already type quickly?",
    answer:
      "Usually, yes. The benefit is not only raw speed. It is the reduced friction between having an idea and getting it onto the screen, which matters most for longer emails, reports, notes, and documents.",
  },
];

const FAQSection = () => {
  // Single-open accordion; every item starts collapsed.
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="faqs"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className="w-full"
    >
      <div
        className={`${CONTAINER} pt-[64px] pb-[64px] md:pt-[88px] md:pb-[88px] lg:pt-[112px] lg:pb-[112px]`}
      >
        <h2 className={H2}>Common Questions About AI Voice Typing</h2>

        <div className="mt-[30.05px] border-t-[1.07px] border-[#0A0D1614]">
          {FAQS.map(({ question, answer }, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={question}
                className="border-b-[1.07px] border-[#0A0D1614]"
              >
                <h3>
                  <button
                    type="button"
                    id={`voice-faq-trigger-${index}`}
                    aria-expanded={isOpen}
                    aria-controls={`voice-faq-panel-${index}`}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 py-[18px] text-left sm:py-[21.46px]"
                  >
                    <span className="font-jakarta text-[15px] font-medium leading-[24px] tracking-[0px] text-[#0A0D16] sm:text-[16.1px] sm:leading-[26.16px]">
                      {question}
                    </span>

                    <Plus
                      className={`h-[18px] w-[18px] shrink-0 text-[#3AB1F6] transition-transform duration-300 ${
                        isOpen ? "rotate-45" : "rotate-0"
                      }`}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </button>
                </h3>

                {/*
                  grid-rows 0fr -> 1fr animates to the answer's natural height
                  without measuring it, and keeps the copy in the DOM so it
                  stays indexable while collapsed.
                */}
                <div
                  id={`voice-faq-panel-${index}`}
                  role="region"
                  aria-labelledby={`voice-faq-trigger-${index}`}
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="max-w-[820px] pb-[18px] pr-[34px] font-inter text-[15px] font-normal leading-[26px] tracking-[0px] text-[#0A0D16B2] sm:pb-[21.46px] sm:text-[16px] sm:leading-[29.6px]">
                      {answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
